import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PublishIcon from "@mui/icons-material/Publish";
import { createPricebookDraft } from "../../../../services";
import {
  PriceBookWithCounts,
  PricebookApplyMode,
} from "../../../../types/price-book.type";
import ConfirmModal from "../../../ConfirmModal";
import {
  priceBookDetailsPath,
  priceBookPeriodsPath,
  priceBookSchedulesPath,
} from "../../shared/constants";
import DeletePriceBookDialog from "../DeletePriceBookDialog";
import { buildStoreBlockerMessage } from "../deleteConflict";
import { PriceBookDeleteTarget } from "../types";
import ApplyDraftDialog from "./ApplyDraftDialog";
import { resolveApplyBlocker } from "./draftFlow";
import { PricebookDraftDiffState } from "./usePricebookDraftDiff";

const SAVE_FIRST_HINT = "บันทึกหรือยกเลิกการแก้ไขก่อน";

/** After an apply the source opens where the result shows: its lines, its schedule, or its new periods. */
const APPLIED_PATH_BY_MODE: Record<PricebookApplyMode, (uuid: string) => string> = {
  now: priceBookDetailsPath,
  date: priceBookSchedulesPath,
  range: priceBookPeriodsPath,
};

const toDeleteTarget = (priceBook: PriceBookWithCounts): PriceBookDeleteTarget => ({
  uuid: priceBook.uuid,
  code: priceBook.code,
  name: priceBook.name,
  status: priceBook.status,
  branchCount: priceBook.branch_count,
  productCount: priceBook.product_count,
  isDraft: priceBook.is_draft,
});

interface PriceBookHeaderActionsProps {
  priceBook: PriceBookWithCounts;
  /** False for the books the system settings name; they are never copied as a draft. */
  canCopyAsDraft: boolean;
  /** The page holds edits nobody saved yet: copying or applying would lose or skip them. */
  hasUnsavedChanges: boolean;
  /** The draft compared with its source; only read on a draft. */
  draftDiff: PricebookDraftDiffState;
  onOpenEditDrawer: () => void;
  /** Something on the book was written, so its header has to be read again. */
  onChanged: () => void;
  onDraftDeleted: () => void;
  /** The API refused the delete; the message says what has to happen first. */
  onRefused: (message: string) => void;
}

/**
 * The header buttons of a book the reader may maintain. A live book is copied as a draft to try
 * in 00-HO, or has its header edited; a draft is thrown away or applied onto its source.
 */
export default function PriceBookHeaderActions({
  priceBook,
  canCopyAsDraft,
  hasUnsavedChanges,
  draftDiff,
  onOpenEditDrawer,
  onChanged,
  onDraftDeleted,
  onRefused,
}: PriceBookHeaderActionsProps) {
  const navigate = useNavigate();
  const [isCopyConfirmOpen, setIsCopyConfirmOpen] = useState<boolean>(false);
  const [copying, setCopying] = useState<boolean>(false);
  const [deleteTarget, setDeleteTarget] = useState<PriceBookDeleteTarget | null>(null);
  const [isApplyOpen, setIsApplyOpen] = useState<boolean>(false);

  const sourceCode = priceBook.source_pricebook_code ?? "";
  // The API refuses to delete a book any shop is on, test shops included.
  const hasShops = priceBook.active_store_count > 0;
  const deleteHint = hasShops ? buildStoreBlockerMessage(priceBook.active_store_count) : "";
  const applyBlocker = resolveApplyBlocker({
    hasUnsavedChanges,
    diff: draftDiff.diff,
    loading: draftDiff.loading,
    errorMessage: draftDiff.errorMessage,
  });
  const copyHint = hasUnsavedChanges ? SAVE_FIRST_HINT : "";
  const copyDescription = `สร้างร่างของ ${priceBook.code} ${priceBook.name} ไว้แก้ราคาและทดสอบกับร้านค้าของ 00-HO สาขายังใช้ราคาเดิมจนกว่าจะนำร่างไปใช้`;

  const handleOpenCopyConfirm = () => setIsCopyConfirmOpen(true);
  const handleCloseCopyConfirm = () => {
    if (copying) return;
    setIsCopyConfirmOpen(false);
  };

  const handleCreateDraft = async () => {
    setCopying(true);
    try {
      const {
        data: { data },
      } = await createPricebookDraft(priceBook.uuid);
      setIsCopyConfirmOpen(false);
      if (data) navigate(priceBookDetailsPath(data.uuid));
    } catch {
      // The interceptor has already shown why; the reader is back on the book to decide.
      setIsCopyConfirmOpen(false);
    } finally {
      setCopying(false);
    }
  };

  const handleAskDelete = () => setDeleteTarget(toDeleteTarget(priceBook));
  const handleCloseDelete = () => setDeleteTarget(null);

  // The comparison is read again so the dialog never shows one older than the click.
  const handleOpenApply = () => {
    draftDiff.reload();
    setIsApplyOpen(true);
  };
  const handleCloseApply = () => setIsApplyOpen(false);
  const handleApplied = (sourceUuid: string, mode: PricebookApplyMode) => {
    setIsApplyOpen(false);
    navigate(APPLIED_PATH_BY_MODE[mode](sourceUuid));
  };

  if (priceBook.is_draft) {
    return (
      <Stack direction="row" gap={1}>
        <Tooltip title={deleteHint}>
          <span>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              disabled={hasShops}
              onClick={handleAskDelete}
            >
              ลบร่าง
            </Button>
          </span>
        </Tooltip>
        <Tooltip title={applyBlocker}>
          <span>
            <Button
              variant="contained"
              startIcon={<PublishIcon />}
              disabled={Boolean(applyBlocker)}
              onClick={handleOpenApply}
            >
              นำไปใช้กับ {sourceCode}
            </Button>
          </span>
        </Tooltip>

        <DeletePriceBookDialog
          target={deleteTarget}
          onClose={handleCloseDelete}
          onDeleted={onDraftDeleted}
          onDeactivated={onChanged}
          onRefused={onRefused}
        />
        <ApplyDraftDialog
          open={isApplyOpen}
          draftUuid={priceBook.uuid}
          draftCode={priceBook.code}
          sourceCode={sourceCode}
          diffState={draftDiff}
          onClose={handleCloseApply}
          onApplied={handleApplied}
        />
      </Stack>
    );
  }

  return (
    <Stack direction="row" gap={1}>
      {canCopyAsDraft && (
        <Tooltip title={copyHint}>
          <span>
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              disabled={hasUnsavedChanges}
              onClick={handleOpenCopyConfirm}
            >
              ทำสำเนาไปทดสอบ (00-HO)
            </Button>
          </span>
        </Tooltip>
      )}
      <Button variant="contained" startIcon={<EditIcon />} onClick={onOpenEditDrawer}>
        แก้ไข
      </Button>

      <ConfirmModal
        open={isCopyConfirmOpen}
        title="ทำสำเนาไปทดสอบ (00-HO)"
        description={copyDescription}
        confirmLabel="สร้างร่าง"
        loading={copying}
        onClose={handleCloseCopyConfirm}
        onConfirm={handleCreateDraft}
      />
    </Stack>
  );
}
